import { Alert, Form, Typography, Select } from 'antd';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import ClientDetailsForm from './ClientDetailsForm';
import { ContainerWithForm, FormContainer } from '../../components/Container';
import { DOCUMENTS } from '../../constants/routes';
import FooterNavigator from '../../components/FooterNavigator';
import { useAppDispatch } from '../../hooks/redux-hooks';
import { addConsentsData, addFormIdData, updateBookingClientData, updateClientHealthSummaryData,
} from '../../redux/slices/booking';
import { CONSENTS_MAPPING } from '../../constants/consents';
import Consents from './Consents';
import { removeEmptyStrFields } from '../../utils/object-check';
import { DATE_WITH_DASH } from '../../constants/time-formats';
import analytics from '../../infra/analytics';
import { NEW_PATIENT_REGISTRATION,
  SITE_NAME,
} from '../../constants/analytics';
import { FONT } from '../../styles/color';
import { StylePageSubtitle, StylePageTitle } from '../../styles/sharedStyleComponets';

const ClientDetails: FC = () => {
  const [isAgreedThePrivacyPolicy, setIsAgreedThePrivacyPolicy] = useState(false);
  const [consentsHasError, setConsentsHasError] = useState(false);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const formIdRef = useRef(uuidv4());
  const formId = formIdRef.current;

  const { pathname } = useLocation();

  useEffect(() => {
    analytics.logPageViewEvent(NEW_PATIENT_REGISTRATION, {
      site: SITE_NAME,
      page_path: pathname,
    });

    analytics.trackVpcFormStart({
      form_id: formId,
    });
  }, [formId, pathname]);

  const submit = useCallback(async () => {
    if (!isAgreedThePrivacyPolicy) {
      setConsentsHasError(true);
      analytics.trackVpcFormError({
        form_id: formId,
        error_type: 'form_validation_error',
        page_path: pathname,
      });
      return false;
    }
    setConsentsHasError(false);

    try {
      const values = await form.validateFields();
      const {
        nextOfKin,
        addressResidential,
        diffPostalAddress,
        others,
        ...otherValues
      } = values;
      const addressPostal = diffPostalAddress
        ? values.addressPostal
        : addressResidential;

      dispatch(
        addFormIdData({ formId }),
      );
      dispatch(
        updateBookingClientData({
          ...removeEmptyStrFields(otherValues),
          addressPostal: removeEmptyStrFields(addressPostal),
          addressResidential: removeEmptyStrFields(addressResidential),
          ...(nextOfKin ? { nextOfKin: removeEmptyStrFields(nextOfKin) } : {}),
          dateOfBirth: values.dateOfBirth.format(DATE_WITH_DASH),
        }),
      );

      dispatch(updateClientHealthSummaryData(removeEmptyStrFields(others)));

      dispatch(
        addConsentsData({
          isAgreedThePrivacyPolicy: {
            ...CONSENTS_MAPPING.isAgreedThePrivacyPolicy,
            agreed: true,
          },
        }),
      );

      return true;
    } catch (errorInfo) {
      analytics.trackVpcFormError({
        form_id: formId,
        error_type: 'form_validation_error',
        page_path: pathname,
      });
      return false;
    }
  }, [isAgreedThePrivacyPolicy, formId, pathname, form, dispatch]);

  // const { search } = useLocation();
  // const searchParams = new URLSearchParams(search);
  // const nextRoute = searchParams.get('specialist') && searchParams.get('specialty') ? DOCTORS_ROUTE : SPECIALITIES_ROUTE;

  return (
    <ContainerWithForm>
      <StylePageTitle level={2}>New Patient Registration</StylePageTitle>
      <StylePageSubtitle>
        Thank you for choosing Amplar Health Virtual Psychology Clinic.
        <br />
        To book your specialist or allied health appointment, please fill out the following form.
      </StylePageSubtitle>
      <Typography.Title level={3} style={{ color: FONT }}>
        Your Contact Details
      </Typography.Title>
      <ClientDetailsForm form={form} />

      <Typography.Title level={3} style={{ color: FONT }}>
        Others
      </Typography.Title>

      <FormContainer layout="vertical" form={form}>
        <Form.Item
          label="Aboriginal and/or Torres Strait Islander"
          required
          name={['others', 'aboriginalOrTorresStraitIslander']}
          rules={[
            {
              required: true,
              message: 'Please select whether Aboriginal and/or Torres Strait Islander',
            },
          ]}
        >
          <Select style={{ width: '100%' }}>
            <Select.Option value="aboriginal">Aboriginal</Select.Option>
            <Select.Option value="not aboriginal and/or torres strait islander">
              Not Aboriginal and/or Torres Strait Islander
            </Select.Option>
            <Select.Option value="aboriginal and torres strait islander">
              Aboriginal and Torres Strait Islander
            </Select.Option>
            <Select.Option value="torres strait islander">Torres Strait Islander</Select.Option>
            <Select.Option value="not stated / inadequately described">
              Not stated / Inadequately described
            </Select.Option>
          </Select>
        </Form.Item>
      </FormContainer>

      <Consents setIsAgreedThePrivacyPolicy={setIsAgreedThePrivacyPolicy} />

      {consentsHasError && (
        <Alert
          message="Please agree to all terms and conditions before continuing."
          type="error"
        />
      )}

      <FooterNavigator
        error=""
        submit={submit}
        next={DOCUMENTS}
        hasBack={false}
        containerStyle={{ marginTop: '40px' }}
      />
    </ContainerWithForm>
  );
};

export default ClientDetails;
